import { matchesProperty, merge, compose, filter, find, get, __, map, has } from 'lodash/fp';
import { createSelector } from 'reselect';
import { createReducer, createAsyncActionTypes } from '@mews/framework';
import { getOrFalse, getStringProp } from '@mews/fp';
import { GeneralEnterpriseSubscriptionState as SubscriptionState } from '@mews-commander/data-access';
import { createFetchAction, keyById } from '@mews-commander/utils';
import moduleSelector from '../selector';
import prefix from '../prefix';
import { META_STATE_SUCCEEDED, META_STATE_FAILED, META_STATE_STARTED } from '../constants';

export const NAME = 'subscriptions';

const initialState = {
    ...META_STATE_SUCCEEDED,
};
export const SUBSCRIPTIONS_LOADED = prefix('SUBSCRIPTIONS_LOADED');

export const SUBSCRIPTIONS_FETCH = prefix('SUBSCRIPTIONS_FETCH');
export const [SUBSCRIPTIONS_FETCH_STARTED, SUBSCRIPTIONS_FETCH_SUCCEEDED, SUBSCRIPTIONS_FETCH_FAILED] =
    createAsyncActionTypes(SUBSCRIPTIONS_FETCH);

export const fetchSubscriptions =
    ({ enterpriseId, profileId }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const promise = container()
            .subscriptionsComponent.getAll({ enterpriseId, profileId })
            .then(({ subscriptions }: $TSFixMe) => subscriptions);
        return dispatch(createFetchAction(SUBSCRIPTIONS_FETCH, { promise }));
    };

export const subscriptionsReducer = createReducer(initialState, {
    // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
    [SUBSCRIPTIONS_LOADED]: (state, payload) => ({
        ...state,
        ...keyById(payload),
    }),
    [SUBSCRIPTIONS_FETCH_STARTED]: merge(__, { ...META_STATE_STARTED }),
    [SUBSCRIPTIONS_FETCH_SUCCEEDED]: (state: $TSFixMe, subscriptions: $TSFixMe) =>
        merge(__, {
            ...META_STATE_SUCCEEDED,
            ...keyById(subscriptions),
        })(state),
    [SUBSCRIPTIONS_FETCH_FAILED]: merge(__, { ...META_STATE_FAILED }),
});

export const getSubscriptions = createSelector(moduleSelector, (module) => module[NAME] || {});

export const getSubscription = createSelector(
    getSubscriptions,
    getStringProp('subscriptionId'),
    (subscriptions, subscriptionId) => subscriptions[subscriptionId] || {}
);

export const hasSubscription = createSelector(getStringProp('subscriptionId'), getSubscriptions, has);

const getSubscriptionByCode = createSelector(getSubscriptions, getStringProp('code'), (subscriptions, code) =>
    find({ code }, subscriptions)
);

export const getSubscriptionIdByCode = compose(get('id'), getSubscriptionByCode);

const matchesSubscriptionStateProperty = matchesProperty('state');

export const isSubscriptionOverdue = createSelector(
    getSubscription,
    matchesSubscriptionStateProperty(SubscriptionState.Overdue)
);

export const getUnusedSubscriptions = compose(filter({ isUsed: false }), getSubscriptions);

export const getSubscriptionTier = compose(get('tier'), getSubscription);

export const getSubscriptionCode = compose(parseInt, get('code'), getSubscription);

export const isContractErrorBannerEnabled = compose(get('contractErrorBannerEnabled'), getSubscription);

export const isContractWarningBannerEnabled = compose(get('contractWarningBannerEnabled'), getSubscription);

const getSubscriptionPackageOptions = compose(get('packageOptions'), getSubscription);

const isPackageOptionEnabled = (optionName: $TSFixMe) => compose(getOrFalse(optionName), getSubscriptionPackageOptions);

export const [
    isActivityReportEnabled,
    areCustomerStatisticsEnabled,
    isCheckoutReportEnabled,
    isIntegrationTierEnabled,
    isMultiEnterpriseNewReservationScreenEnabled,
    arePrivateMessageThreadsEnabled,
    isRoomingListEnabled,
    isSpaceAllocationOptimizationEnabled,
    isGroupBillingEnabled,
    isAvailabilityBlockBookingEngineLinksEnabled,
    isAvailabilityBlockAdvancedFeaturesEnabled,
    isKioskEnabled,
    isSmartTipsEnabled,
    areCustomLinksEnabled,
    areCalendarEventsEnabled,
    areVouchersEnabled,
    areNewActionLogsEnabled,
    isRoleManagementEnabled,
    isBillingTemplatesEnabled,
    isPaymentAndCancellationPoliciesExceptionsEnabled,
] = map(isPackageOptionEnabled, [
    'activityReportEnabled',
    'customerStatisticsEnabled',
    'checkoutReportEnabled',
    'integrationTierEnabled',
    'multiEnterpriseNewReservationScreenEnabled',
    'privateMessageThreadsEnabled',
    'roomingListEnabled',
    'spaceAllocationOptimizationEnabled',
    'groupBillingEnabled',
    'availabilityBlockBookingEngineLinksEnabled',
    'availabilityBlockAdvancedFeaturesEnabled',
    'kioskEnabled',
    'smartTipsEnabled',
    'customLinksEnabled',
    'calendarEventsEnabled',
    'vouchersEnabled',
    'extendedActionLogForServiceOrdersAndBillsAndOrderItems',
    'roleManagementEnabled',
    'billingTemplatesEnabled',
    'paymentAndCancellationPoliciesExceptionsEnabled',
]);

const getSubscriptionOperationModes = compose(get('operationModes'), getSubscription);

export const hasSubscriptionApplicationProviderMode = compose(
    getOrFalse('isApplicationProvider'),
    getSubscriptionOperationModes
);

export const hasSubscriptionPortfolioMode = compose(getOrFalse('isPortfolio'), getSubscriptionOperationModes);

export const hasSubscriptionServiceProviderMode = compose(
    getOrFalse('isServiceProvider'),
    getSubscriptionOperationModes
);
